/**
 * Performance Metrics Display
 * Shows real-time context update frequency and performance metrics
 * Helps identify potential infinite loops early
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  Card,
  CardContent,
  IconButton,
  Collapse,
  Alert,
  AlertTitle
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { contextUpdateMonitor } from './ContextUpdateMonitor';

interface PerformanceMetricsProps {
  contextNames?: string[];
  showInProduction?: boolean;
}

export const PerformanceMetrics: React.FC<PerformanceMetricsProps> = ({
  contextNames = [],
  showInProduction = false
}) => {
  const [expanded, setExpanded] = useState(false);
  const [metrics, setMetrics] = useState(contextUpdateMonitor.getAllMetrics());
  const [updateCount, setUpdateCount] = useState(0);

  // Don't show in production unless explicitly enabled
  if (import.meta.env.PROD && !showInProduction) {
    return null;
  }

  useEffect(() => {
    const interval = setInterval(() => {
      const currentMetrics = contextUpdateMonitor.getAllMetrics();
      setMetrics(currentMetrics);
      setUpdateCount(prev => prev + 1);
    }, 1000); // Update every second

    return () => clearInterval(interval);
  }, []);

  const filteredMetrics = contextNames.length > 0 
    ? metrics.filter(m => contextNames.includes(m.contextName))
    : metrics;

  const hasErrors = filteredMetrics.some(m => m.isError);
  const hasWarnings = filteredMetrics.some(m => m.isWarning);

  const getStatusColor = () => {
    if (hasErrors) return 'error';
    if (hasWarnings) return 'warning';
    return 'success';
  };

  const getStatusIcon = () => {
    if (hasErrors) return <ErrorIcon />;
    if (hasWarnings) return <WarningIcon />;
    return <CheckCircleIcon />;
  };

  const formatUpdateFrequency = (metrics: any) => {
    if (metrics.averageUpdateInterval === 0) return 'N/A';
    const frequency = 1000 / metrics.averageUpdateInterval;
    return `${frequency.toFixed(2)}/sec`;
  };

  return (
    <Card 
      variant="outlined" 
      sx={{ 
        position: 'fixed',
        bottom: 16,
        right: 16,
        width: expanded ? 400 : 200,
        zIndex: 1000,
        transition: 'width 0.3s ease'
      }}
    >
      <CardContent sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getStatusIcon()}
            <Typography variant="subtitle2">
              Context Monitor
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        <Collapse in={expanded}>
          <Box sx={{ mt: 2 }}>
            {filteredMetrics.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No context activity detected
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {filteredMetrics.map((metric) => (
                  <Box key={metric.contextName}>
                    <Typography variant="caption" fontWeight="medium">
                      {metric.contextName}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                      <Chip
                        size="small"
                        label={`${metric.updateCount} updates`}
                        color={metric.isError ? 'error' : metric.isWarning ? 'warning' : 'default'}
                        variant="outlined"
                      />
                      <Chip
                        size="small"
                        label={formatUpdateFrequency(metric)}
                        color={metric.isError ? 'error' : metric.isWarning ? 'warning' : 'default'}
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            )}

            {hasErrors && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <AlertTitle>High Update Frequency Detected</AlertTitle>
                One or more contexts are updating very frequently. This may indicate an infinite loop.
              </Alert>
            )}

            {hasWarnings && !hasErrors && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <AlertTitle>Performance Warning</AlertTitle>
                Context update frequency is higher than recommended.
              </Alert>
            )}

            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption" color="text.secondary">
                Update #{updateCount}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {new Date().toLocaleTimeString()}
              </Typography>
            </Box>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default PerformanceMetrics;
